import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../context/LangContext";

const COPY = {
  fr: {
    eyebrow: "01 — UNIVERS",
    title: "Un protocole pour chaque exigence.",
    subtitle: "Danseurs, acteurs, sportifs, esthétique, régénération cellulaire — neuf univers, une même exigence.",
    caption: "Faites glisser pour explorer",
  },
  en: {
    eyebrow: "01 — UNIVERSES",
    title: "One protocol for every demand.",
    subtitle: "Dancers, actors, athletes, aesthetics, cellular regeneration — nine universes, one standard.",
    caption: "Drag to explore",
  },
  it: {
    eyebrow: "01 — UNIVERSI",
    title: "Un protocollo per ogni esigenza.",
    subtitle: "Ballerini, attori, atleti, estetica, rigenerazione cellulare — nove universi, un'unica esigenza.",
    caption: "Trascina per esplorare",
  },
  ru: {
    eyebrow: "01 — ВСЕЛЕННЫЕ",
    title: "Протокол для любых задач.",
    subtitle: "Танцоры, актёры, спортсмены, эстетика, клеточная регенерация — девять вселенных, один стандарт.",
    caption: "Листайте, чтобы открыть",
  },
};

const PANELS = [
  { img: "/univers/univers-1.jpg", fr: "Danseur", en: "Dancer", it: "Ballerino", ru: "Танцор" },
  { img: "/univers/univers-2.jpg", fr: "Acteur", en: "Actor", it: "Attore", ru: "Актёр" },
  { img: "/univers/univers-3.jpg", fr: "Sportif", en: "Athlete", it: "Atleta", ru: "Спортсмен" },
  { img: "/univers/univers-4.jpg", fr: "Esthétique non invasive", en: "Non-invasive aesthetics", it: "Estetica non invasiva", ru: "Неинвазивная эстетика" },
  { img: "/univers/univers-5.jpg", fr: "Alignement corps & technologies", en: "Body & technology alignment", it: "Allineamento corpo & tecnologie", ru: "Союз тела и технологий" },
  { img: "/univers/univers-6.jpg", fr: "Mobilité & confort corporel", en: "Mobility & body comfort", it: "Mobilità & comfort corporeo", ru: "Подвижность и комфорт тела" },
  { img: "/univers/univers-7.jpg", fr: "Équilibre émotionnel & énergétique", en: "Emotional & energetic balance", it: "Equilibrio emotivo & energetico", ru: "Эмоциональный и энергетический баланс" },
  { img: "/univers/univers-8.jpg", fr: "Le lit plasma", en: "The plasma bed", it: "Il letto al plasma", ru: "Плазменная капсула" },
  { img: "/univers/univers-9.jpg", fr: "Expériences sur-mesure", en: "Tailored experiences", it: "Esperienze su misura", ru: "Индивидуальные впечатления" },
];

export default function UniversCarousel() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);

  const scrollBy = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector("[data-panel]");
    const step = card ? card.offsetWidth + 16 : 300;
    track.scrollBy({ left: dir * step * 2, behavior: "smooth" });
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector("[data-panel]");
    const step = card ? card.offsetWidth + 16 : 300;
    setIndex(Math.round(track.scrollLeft / step));
  };

  return (
    <section className="py-24 md:py-32" data-testid="section-univers">
      <div className="container-luxe">
        <motion.div
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-70px" }}
          transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-end justify-between gap-6 flex-wrap"
        >
          <div className="max-w-3xl">
            <div className="overline mb-4">{c.eyebrow}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[var(--ink)] leading-[1.05]">
              {c.title}
            </h2>
            <p className="mt-5 text-[15px] sm:text-base text-[var(--text-secondary)] font-body leading-relaxed">{c.subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scrollBy(-1)}
              className="w-11 h-11 rounded-full border border-[var(--ink)]/15 text-[var(--ink)] hover:border-[var(--gold)] hover:text-[var(--gold)] flex items-center justify-center transition-colors"
              aria-label="previous"
              data-testid="univers-prev"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => scrollBy(1)}
              className="w-11 h-11 rounded-full border border-[var(--ink)]/15 text-[var(--ink)] hover:border-[var(--gold)] hover:text-[var(--gold)] flex items-center justify-center transition-colors"
              aria-label="next"
              data-testid="univers-next"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="mt-12 overflow-hidden"
      >
        <div
          ref={trackRef}
          onScroll={onScroll}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 px-6 md:px-[max(24px,calc((100vw-1280px)/2+56px))] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="univers-track"
        >
          {PANELS.map((p, i) => (
            <figure key={p.img} data-panel className="snap-start shrink-0 w-[240px] md:w-[290px] scroll-ml-6 md:scroll-ml-[calc((100vw-1280px)/2+56px)] group" data-testid={`univers-panel-${i + 1}`}>
              <div className="relative aspect-[2/3] overflow-hidden hairline rounded-md bg-[var(--ink)]">
                <img
                  src={p.img}
                  alt={p[lang] || p.en}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
                <span className="absolute top-3 right-3 text-[10px] font-display tracking-[0.2em] text-white/70 bg-black/30 backdrop-blur-sm rounded-full px-2.5 py-1">
                  {i + 1}/9
                </span>
              </div>
              <figcaption className="mt-3 flex items-baseline justify-between gap-3">
                <span className="font-display text-sm text-[var(--ink)] font-light leading-snug">{p[lang] || p.en}</span>
                <span className="overline !text-[var(--ink)]/30">0{i + 1}</span>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="container-luxe mt-2 flex items-center gap-3">
          <div className="flex gap-1.5">
            {PANELS.map((_, i) => (
              <span key={i} className={`h-1 rounded-full transition-all duration-300 ${Math.min(index, 8) === i ? "w-6 bg-[var(--gold)]" : "w-2 bg-[var(--ink)]/15"}`} />
            ))}
          </div>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ink)]/40 font-display">{c.caption}</span>
        </div>
      </motion.div>
    </section>
  );
}
